// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { Cart } from "@insite/client-framework/Services/CartService";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { CountryModel, CustomerValidationDto, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { ValidateAddressResult } from "@mcmc/App/Services/AccountCreationService";
import { CheckoutStepKey } from "@mcmc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmit.helper";
import { Draft } from "immer";

export interface CustomCheckoutShippingState {
    overrideShipTo: boolean;
    overrideAddressFormState?: {
        address: ShipToModel;
        formErrors: {
            [key: string]: React.ReactNode;
        };
    };
    validateAddressResult?: ValidateAddressResult;
    showValidationModal: boolean;
    nextStepHash?: CheckoutStepKey;
    requestedPickupDate?: Date | null;
    billToEmailAddressError?: React.ReactNode;
}

const initialState: CustomCheckoutShippingState = {
    overrideShipTo: false,
    showValidationModal: false,
};

const xCustomCheckoutShipping = createTypedReducerWithImmer(initialState, {
    "Pages/CheckoutShipping/SetInitialValues": (
        draft: Draft<CustomCheckoutShippingState>,
        action: {
            cart: Cart;
            shipTo: ShipToModel;
            country: CountryModel | undefined;
        },
    ) => {
        let address;
        if (action.cart.properties.overrideShipToAddress) {
            address = { ...JSON.parse(action.cart.properties.overrideShipToAddress) };
            overrideValidationRules(address.validation);
            draft.overrideShipTo = true;
        }

        draft.overrideAddressFormState = {
            address,
            formErrors: {},
        };
    },
    "Pages/CheckoutShipping/SetRequestedPickUpDate": (
        draft: Draft<CustomCheckoutShippingState>,
        action: { requestedPickUpDate?: Date },
    ) => {
        draft.requestedPickupDate = action.requestedPickUpDate;
    },
    "CustomCheckoutShipping/SetOverrideAddress": (
        draft: Draft<CustomCheckoutShippingState>,
        action: { address: ShipToModel },
    ) => {
        if (!draft.overrideAddressFormState) {
            return;
        }

        draft.overrideAddressFormState.address = action.address;
    },
    "CustomCheckoutShipping/SetOverrideAddressFormErrors": (
        draft: Draft<CustomCheckoutShippingState>,
        action: { formErrors: { [key: string]: React.ReactNode } },
    ) => {
        if (!draft.overrideAddressFormState) {
            return;
        }

        draft.overrideAddressFormState.formErrors = action.formErrors;
    },
    "CustomCheckoutShipping/SetBillToEmailAddressFormError": (
        draft: Draft<CustomCheckoutShippingState>,
        action: { emailError: React.ReactNode },
    ) => {
        draft.billToEmailAddressError = action.emailError;
    },
    "CustomCheckoutShipping/SetOverrideShipTo": (
        draft: Draft<CustomCheckoutShippingState>,
        action: {
            overrideShipTo: boolean;
            address: ShipToModel | undefined;
        },
    ) => {
        draft.overrideShipTo = action.overrideShipTo;

        if (!draft.overrideAddressFormState || !action.address) {
            return;
        }
        overrideValidationRules(action.address.validation);
        draft.overrideAddressFormState.address = { ...action.address };
        draft.overrideAddressFormState.formErrors = {};
    },
    "CustomCheckoutShipping/SetOverrideValidationResult": (
        draft: Draft<CustomCheckoutShippingState>,
        action: {
            validationResult: ValidateAddressResult;
        },
    ) => {
        draft.validateAddressResult = action.validationResult;
        if (action.validationResult.isValid && action.validationResult.normalizedAddresses.length > 0) {
            draft.showValidationModal = true;
        }
    },
    "CustomCheckoutShipping/HideValidationModal": (draft: Draft<CustomCheckoutShippingState>) => {
        draft.showValidationModal = false;
    },
    "CustomCheckoutShipping/SetNextCheckoutStep": (
        draft: Draft<CustomCheckoutShippingState>,
        action: {
            nextStepHash: CheckoutStepKey;
        },
    ) => {
        draft.nextStepHash = action.nextStepHash;
    },
});

function overrideValidationRules(validation: CustomerValidationDto | null) {
    if (validation) {
        validation.address1 && (validation.address1.isDisabled = false);
        validation.address2 && (validation.address2.isDisabled = false);
        validation.address3 && (validation.address3.isDisabled = false);
        validation.address4 && (validation.address4.isDisabled = false);
        validation.attention && (validation.attention.isDisabled = false);
        validation.city && (validation.city.isDisabled = false);
        validation.companyName && (validation.companyName.isDisabled = false);
        validation.country && (validation.country.isDisabled = false);
        validation.email && (validation.email.isDisabled = false);
        validation.firstName && (validation.firstName.isDisabled = false);
        validation.lastName && (validation.lastName.isDisabled = false);
        validation.phone && (validation.phone.isDisabled = false);
        validation.fax && (validation.fax.isDisabled = false);
        validation.postalCode && (validation.postalCode.isDisabled = false);
        validation.state && (validation.state.isDisabled = false);

        validation.address1 && (validation.address1.isRequired = true);
        validation.address2 && (validation.address2.isRequired = false);
        validation.address3 && (validation.address3.isRequired = false);
        validation.address4 && (validation.address4.isRequired = false);
        validation.attention && (validation.attention.isRequired = false);
        validation.city && (validation.city.isRequired = true);
        validation.companyName && (validation.companyName.isRequired = true);
        validation.country && (validation.country.isRequired = false);
        validation.email && (validation.email.isRequired = true);
        validation.firstName && (validation.firstName.isRequired = false);
        validation.lastName && (validation.lastName.isRequired = false);
        validation.phone && (validation.phone.isRequired = false);
        validation.fax && (validation.fax.isRequired = false);
        validation.postalCode && (validation.postalCode.isRequired = true);
        validation.state && (validation.state.isRequired = false);
    }
}

(reducers as any).xCustomCheckoutShipping = xCustomCheckoutShipping;

type xCustomCheckoutShippingReducers = {
    BrandFilters: typeof xCustomCheckoutShipping;
};

export type xCustomCheckoutShippingActions =
    | Parameters<xCustomCheckoutShippingReducers[keyof xCustomCheckoutShippingReducers]>[1]
    | AnyAction;
